import { useEffect, useState } from "react";
import { Stack, Paper, SvgIcon, Box, Grid, Typography } from "@mui/material";
import { LoadingButton as Button } from "@mui/lab";
import _ from "lodash";
import { useParams, useLocation } from "react-router-dom";
import {
  SystemUpdateAlt as DownloadIcon,
  WhatsApp as WhatsAppIcon,
  ForwardToInboxOutlined as EmailIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { useApi, useIsInQuickLink, useHealthCenterRules } from "../../utils/hooks";
import MessageSuccess from "./MessageSucces";
import DataDetails from "./DataDetails";
import LoadingSpinner from "../../../src/utils/LoadingSpinner";
import Exporter from "./Exporter";
import { saveAs } from "file-saver";
import TitleHeadSess from "../../utils/TitleHeadSess";
import { ErrorAlert, Alert as CustomAlert } from "../../utils";
import CancelButton from "./CancelButton";
import RepeaterButton from "./RepeaterButton";
import Promise from "bluebird";
import useFirstMedicalDocument from "./hooks/useFirstMedicalDocument";

const StatusRow = ({ Icon, color, children }) => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 1, gap: 0.5 }}>
    <Icon color={color} sx={{ fontSize: 18 }} />
    <Typography variant="body2" color={`${color}.main`}>
      {children}
    </Typography>
  </Box>
);

const SingleDocumentActions = ({ medicalDocuments, ...props }) => {
  const medicalDocument = useFirstMedicalDocument(medicalDocuments);

  if (_.size(medicalDocuments) !== 1) return;

  return (
    <>
      <RepeaterButton {...props} medicalDocument={medicalDocument} />
      <CancelButton {...props} medicalDocument={medicalDocument} />
    </>
  );
};

const DetailsDocument = () => {
  const { id } = useParams();
  const location = useLocation();
  const [failed, setFailed] = useState(false);
  const { createdMedicalDocuments, showSuccess, backPath } =
    location.state || {};
  const api = useApi();
  const [medicalDocuments, _setMedicalDocuments] = useState();
  const setMedicalDocuments = (medicalDocuments) =>
    _setMedicalDocuments(_.castArray(medicalDocuments));
  const [method, setMethod] = useState("pdf");
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [failedDocumentDetails, setFailedDocumentDetails] = useState(false);
  const [autoShareStatus, setAutoShareStatus] = useState("idle"); // 'idle' | 'loading' | 'done' | 'failed'
  const [manualShareDone, setManualShareDone] = useState(false);
  const isInQuickLink = useIsInQuickLink();
  const medicalDocument = useFirstMedicalDocument(medicalDocuments);
  const medicalDocumentsRules = useHealthCenterRules("medicalDocuments");
  const autoShareEnabled = !!medicalDocumentsRules.share?.whatsapp?.autoShare;
  const patientPhone = medicalDocument?.patient?.phone;
  const isNewAutoShare = showSuccess && autoShareEnabled && !!patientPhone;

  const toggleOpen = () => setOpen(!open);

  useEffect(() => {
    if (createdMedicalDocuments) {
      setMedicalDocuments(createdMedicalDocuments);
      setAutoShareStatus("idle");
      setManualShareDone(false);
      setIsLoading(false);
    } else {
      if (id) {
        api
          .medicalDocument(id)
          .tap((medicalDocument) => setMedicalDocuments(medicalDocument))
          .tap(() => setIsLoading(false))
          .catch(() => {
            setFailedDocumentDetails(true);
            setIsLoading(false);
          });
      }
    }
  }, [createdMedicalDocuments, id]);

  useEffect(() => {
    if (!isNewAutoShare || !medicalDocuments || autoShareStatus !== "idle") return;
    setAutoShareStatus("loading");
    api
      .share(_.map(medicalDocuments, "id"), { method: "whatsapp", destination: patientPhone })
      .then(() => setAutoShareStatus("done"))
      .catch(() => setAutoShareStatus("failed"));
  }, [medicalDocuments]);

  if (isLoading) return <LoadingSpinner isLoading={isLoading} absolute />;

  if (failedDocumentDetails)
    return (
      <ErrorAlert
        show={failedDocumentDetails}
        message="La orden o receta no existe"
      />
    );

  const saveDocument = () => {
    setIsSaving(true);
    Promise.each(medicalDocuments, ({ id, url }) => {
      const fileName = id;
      return fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          saveAs(blob, `${fileName}.pdf`);
        });
    })
      .catch((error) => setFailed(true))
      .finally(() => setIsSaving(false));
  };

  const onShare = (method) => {
    setMethod(method);
    toggleOpen();
  };

  const autoShareSucceeded = autoShareStatus === "done" && !manualShareDone;
  const sharedPhone = autoShareSucceeded && patientPhone;

  const exportButtons = [
    {
      name: "Descargar PDF",
      variant: "outlined",
      icon: <DownloadIcon />,
      onClick: saveDocument,
      loading: isSaving,
    },
    {
      name: "Enviar por Whatsapp",
      variant: "contained",
      icon: <WhatsAppIcon />,
      onClick: () => onShare("ownWhatsapp"),
      loading: autoShareStatus === "loading",
      showCheck: autoShareSucceeded,
    },
    {
      name: "Enviar por E-Mail",
      variant: "contained",
      icon: <EmailIcon />,
      onClick: () => onShare("email"),
      disabled: autoShareStatus === "loading",
    },
  ];

  const typeText = medicalDocument.type === "prescription" ? "receta" : "orden";
  const title = `Detalle de ${typeText} | `;
  const urlRedirect = backPath || "/prescriptions";
  const iconTitle = backPath == "/" ? "Ir al Inicio" : "Volver";
  const messageSuccess =
    showSuccess && `La ${typeText} ha sido generada con éxito!`;

  return (
    <Stack
      width="100%"
      spacing="1rem"
      sx={{ p: { xs: "1rem", sm: "2rem" }, mt: { xs: "1rem" } }}
    >
      <Box sx={{ display: "flex" }}>
        <TitleHeadSess
          title={title}
          iconTitle={iconTitle}
          id={_.map(medicalDocuments, "id")}
          urlRedirect={urlRedirect}
        />

        <SingleDocumentActions
          medicalDocuments={medicalDocuments}
          setMedicalDocument={setMedicalDocuments}
          backPath={backPath}
        />
      </Box>

      <Paper
        variant="outlined"
        width="100%"
        sx={{
          p: { xs: "20px 10px", sm: "20px 40px" },
          borderRadius: 3,
          mb: 7,
        }}
      >
        <Grid item container spacing={3}>
          {showSuccess && (
            <Grid item sm={12}>
              <MessageSuccess messageSuccess={messageSuccess} />
            </Grid>
          )}

          <Grid item sm={12}>
            <DataDetails medicalDocuments={medicalDocuments} />
          </Grid>
        </Grid>
      </Paper>
      <Box sx={{ pb: 5 }}>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "column", md: "row" },
            justifyContent: "center",
          }}
        >
          {exportButtons.map(({ variant, name, icon, onClick, loading, disabled, showCheck }) => (
            <Button
              key={name}
              variant={variant}
              sx={{ mt: 1, mr: 1, ml: 1 }}
              onClick={onClick}
              loading={loading}
              disabled={disabled}
            >
              {name}
              <SvgIcon sx={{ fontSize: 20, ml: 1 }}>{icon}</SvgIcon>
              {showCheck && <CheckCircleIcon sx={{ fontSize: 20, ml: 1, color: "success.light" }} />}
            </Button>
          ))}
        </Box>
        {sharedPhone && (
          <StatusRow Icon={CheckCircleIcon} color="success">
            Prescripción compartida por Whatsapp al {sharedPhone}
          </StatusRow>
        )}
        {autoShareStatus === "failed" && !manualShareDone && (
          <StatusRow Icon={WarningAmberIcon} color="warning">
            No se pudo enviar automáticamente por Whatsapp. Podés intentar con otro número o método de envío.
          </StatusRow>
        )}
      </Box>
      {isInQuickLink && (
        <Stack alignItems="center">
          <Typography>
            Una vez que compartas el documento, podrás cerrar esta ventana.
          </Typography>
        </Stack>
      )}
      <Exporter
        medicalDocuments={medicalDocuments}
        method={method}
        onClose={toggleOpen}
        open={open}
        setMethod={setMethod}
        setFailed={setFailed}
        failed={failed}
        onShared={autoShareEnabled ? () => setManualShareDone(true) : undefined}
      />
    </Stack>
  );
};

export default DetailsDocument;
